import { createAsyncThunk } from "@reduxjs/toolkit";
import { $apiV1, $apiV2 } from "../../config/api";
import { getShadowTeamPlayerPositionGroups } from "../../pages/shadowTeam/utils/playerSearch";
import { trackShadowTeamAction } from "../../tracking";

const ensureArray = (value) => (Array.isArray(value) ? value : []);

const withShadowTeamSearchPlayerStats = (player, statsPlayer) => {
  if (!statsPlayer || typeof statsPlayer !== "object") {
    return player;
  }

  const rawPhotos = ensureArray(player?.photos);
  const statsPhotos = ensureArray(statsPlayer?.photos);

  return {
    ...statsPlayer,
    ...player,
    positions: player?.positions ?? statsPlayer?.positions,
    position: player?.position ?? statsPlayer?.position,
    main_position: player?.main_position ?? statsPlayer?.main_position,
    mainPosition: player?.mainPosition ?? statsPlayer?.mainPosition,
    primary_position: player?.primary_position ?? statsPlayer?.primary_position,
    primaryPosition: player?.primaryPosition ?? statsPlayer?.primaryPosition,
    position_name: player?.position_name ?? statsPlayer?.position_name,
    positionName: player?.positionName ?? statsPlayer?.positionName,
    position_label: player?.position_label ?? statsPlayer?.position_label,
    positionLabel: player?.positionLabel ?? statsPlayer?.positionLabel,
    position_short_name: player?.position_short_name ?? statsPlayer?.position_short_name,
    positionShortName: player?.positionShortName ?? statsPlayer?.positionShortName,
    position_code: player?.position_code ?? statsPlayer?.position_code,
    positionCode: player?.positionCode ?? statsPlayer?.positionCode,
    team: player?.team ?? statsPlayer?.team,
    current_team: player?.current_team ?? statsPlayer?.current_team,
    currentTeam: player?.currentTeam ?? statsPlayer?.currentTeam,
    club: player?.club ?? statsPlayer?.club,
    photos: rawPhotos.length ? rawPhotos : statsPhotos,
    photo:
      player?.photo ||
      statsPlayer?.photo ||
      statsPhotos.find((photo) => photo?.full || photo?.image)?.full ||
      statsPhotos.find((photo) => photo?.full || photo?.image)?.image ||
      null,
  };
};

const enrichShadowTeamSearchResults = async (payload) => {
  const results = Array.isArray(payload)
    ? payload
    : Array.isArray(payload?.results)
    ? payload.results
    : Array.isArray(payload?.data?.results)
    ? payload.data.results
    : Array.isArray(payload?.data)
    ? payload.data
    : null;

  if (!Array.isArray(results) || !results.length) {
    return payload;
  }

  const enrichedResults = await Promise.all(
    results.map(async (player) => {
      if (getShadowTeamPlayerPositionGroups(player).size > 0) {
        return player;
      }

      const playerId = Number(player?.id);

      if (!Number.isFinite(playerId)) {
        return player;
      }

      try {
        const statsResponse = await $apiV1.get(`/statistics/player_opt/${playerId}`);
        return withShadowTeamSearchPlayerStats(player, statsResponse?.data?.player);
      } catch {
        return player;
      }
    })
  );

  if (Array.isArray(payload)) {
    return enrichedResults;
  }

  if (Array.isArray(payload?.results)) {
    return {
      ...payload,
      results: enrichedResults,
    };
  }

  if (Array.isArray(payload?.data?.results)) {
    return {
      ...payload,
      data: {
        ...payload.data,
        results: enrichedResults,
      },
    };
  }

  if (Array.isArray(payload?.data)) {
    return {
      ...payload,
      data: enrichedResults,
    };
  }

  return payload;
};

export const fetchShadowTeamPlayerStats = createAsyncThunk(
  "shadowTeam/fetchPlayerStats",
  async (playerId, { rejectWithValue }) => {
    try {
      const response = await $apiV1.get(`/statistics/player_opt/${playerId}`);
      return { playerId, data: response?.data };
    } catch (error) {
      return rejectWithValue(
        error?.response?.data?.message || error?.message || "Failed to load player stats."
      );
    }
  }
);

const getErrorMessage = (error, fallback) =>
  error?.response?.data?.message || error?.message || fallback;

export const fetchShadowTeamFormations = createAsyncThunk(
  "shadowTeam/fetchFormations",
  async (_, { rejectWithValue }) => {
    try {
      const response = await $apiV2.get("/users/shadow-team-formations");
      return response?.data;
    } catch (error) {
      return rejectWithValue(getErrorMessage(error, "Failed to load shadow team formations."));
    }
  }
);

export const fetchShadowTeams = createAsyncThunk(
  "shadowTeam/fetchTeams",
  async (_, { rejectWithValue }) => {
    try {
      const response = await $apiV2.get("/users/shadow-teams");
      return response?.data;
    } catch (error) {
      return rejectWithValue(getErrorMessage(error, "Failed to load shadow teams."));
    }
  }
);

export const fetchShadowTeamById = createAsyncThunk(
  "shadowTeam/fetchTeamById",
  async (id, { rejectWithValue }) => {
    try {
      const response = await $apiV2.get(`/users/shadow-teams/${id}`);
      return response?.data;
    } catch (error) {
      return rejectWithValue(getErrorMessage(error, "Failed to load shadow team details."));
    }
  }
);

export const createShadowTeam = createAsyncThunk(
  "shadowTeam/createTeam",
  async (payload, { rejectWithValue }) => {
    try {
      const response = await $apiV2.post("/users/shadow-teams", payload);
      trackShadowTeamAction({
        action: "create",
        metadata: {
          formation_id: payload?.formation_id,
        },
        shadowTeamId: response?.data?.id,
        shadowTeamName: response?.data?.name || payload?.name || null,
        sourceSurface: "shadow_team",
      });
      return response?.data;
    } catch (error) {
      return rejectWithValue(getErrorMessage(error, "Failed to create shadow team."));
    }
  }
);

export const updateShadowTeam = createAsyncThunk(
  "shadowTeam/updateTeam",
  async ({ id, payload }, { rejectWithValue }) => {
    try {
      const response = await $apiV2.put(`/users/shadow-teams/${id}`, payload);
      trackShadowTeamAction({
        action: "update",
        metadata: {
          formation_id: payload?.formation_id,
        },
        shadowTeamId: Number(id),
        shadowTeamName: response?.data?.name || payload?.name || null,
        sourceSurface: "shadow_team",
      });
      return response?.data;
    } catch (error) {
      return rejectWithValue(getErrorMessage(error, "Failed to update shadow team."));
    }
  }
);

export const deleteShadowTeam = createAsyncThunk(
  "shadowTeam/deleteTeam",
  async (id, { rejectWithValue }) => {
    try {
      const response = await $apiV2.delete(`/users/shadow-teams/${id}`);
      trackShadowTeamAction({
        action: "delete",
        shadowTeamId: Number(id),
        sourceSurface: "shadow_team",
      });
      return {
        id,
        data: response?.data,
      };
    } catch (error) {
      return rejectWithValue(getErrorMessage(error, "Failed to delete shadow team."));
    }
  }
);

export const addShadowTeamPlayer = createAsyncThunk(
  "shadowTeam/addPlayer",
  async (
    { shadowTeamId, playerId, positionGroup, positionOrder, lineIndex, positionIndex },
    { rejectWithValue }
  ) => {
    try {
      const payload = {
        player_id: playerId,
      };

      if (positionGroup !== undefined && positionGroup !== null) {
        payload.position_group = positionGroup;
      }

      if (positionOrder !== undefined && positionOrder !== null) {
        payload.position_order = positionOrder;
      }

      if (
        payload.position_group === undefined &&
        lineIndex !== undefined &&
        lineIndex !== null &&
        positionIndex !== undefined &&
        positionIndex !== null
      ) {
        payload.line_index = lineIndex;
        payload.position_index = positionIndex;
      }

      const response = await $apiV2.post(`/users/shadow-teams/${shadowTeamId}/players`, payload);
      trackShadowTeamAction({
        action: "player_add",
        metadata: {
          position_group: payload.position_group,
          position_order: payload.position_order,
          position_index: payload.position_index,
          slot_key:
            payload.position_group !== undefined && payload.position_order !== undefined
              ? `${payload.position_group}:${payload.position_order}`
              : null,
        },
        playerId: Number(playerId),
        shadowTeamId: Number(shadowTeamId),
        sourceSurface: "shadow_team",
      });

      return {
        shadowTeamId,
        data: response?.data,
      };
    } catch (error) {
      return rejectWithValue(getErrorMessage(error, "Failed to add player to shadow team."));
    }
  }
);

export const updateShadowTeamPlayerPlacement = createAsyncThunk(
  "shadowTeam/updatePlayerPlacement",
  async ({ shadowTeamId, playerId, positionGroup, positionOrder }, { rejectWithValue }) => {
    try {
      const response = await $apiV2.put(
        `/users/shadow-teams/${shadowTeamId}/players/${playerId}`,
        {
          position_group: positionGroup,
          position_order: positionOrder,
        }
      );
      trackShadowTeamAction({
        action: "player_move",
        metadata: {
          position_group: positionGroup,
          position_order: positionOrder,
          slot_key:
            positionGroup !== undefined && positionOrder !== undefined
              ? `${positionGroup}:${positionOrder}`
              : null,
        },
        playerId: Number(playerId),
        shadowTeamId: Number(shadowTeamId),
        sourceSurface: "shadow_team",
      });

      return {
        shadowTeamId,
        playerId,
        data: response?.data,
      };
    } catch (error) {
      return rejectWithValue(getErrorMessage(error, "Failed to update shadow team player placement."));
    }
  }
);

export const removeShadowTeamPlayer = createAsyncThunk(
  "shadowTeam/removePlayer",
  async ({ shadowTeamId, playerId }, { rejectWithValue }) => {
    try {
      const response = await $apiV2.delete(`/users/shadow-teams/${shadowTeamId}/players/${playerId}`);
      trackShadowTeamAction({
        action: "player_remove",
        playerId: Number(playerId),
        shadowTeamId: Number(shadowTeamId),
        sourceSurface: "shadow_team",
      });

      return {
        shadowTeamId,
        playerId,
        data: response?.data,
      };
    } catch (error) {
      return rejectWithValue(getErrorMessage(error, "Failed to remove player from shadow team."));
    }
  }
);

export const searchShadowTeamPlayers = createAsyncThunk(
  "shadowTeam/searchPlayers",
  async (query, { rejectWithValue }) => {
    try {
      const response = await $apiV1.get("/platform/player/", {
        params: {
          search: query,
          page_size: 20,
        },
      });

      const enrichedResults = await enrichShadowTeamSearchResults(response?.data);
      const resultsCount = Array.isArray(enrichedResults?.results)
        ? enrichedResults.results.length
        : Array.isArray(enrichedResults)
          ? enrichedResults.length
          : 0;

      trackShadowTeamAction({
        action: "search",
        metadata: {
          query,
          results_count: resultsCount,
        },
        sourceSurface: "shadow_team",
      });

      return enrichedResults;
    } catch (error) {
      return rejectWithValue(getErrorMessage(error, "Failed to search players."));
    }
  }
);

export const exportShadowTeamPdf = createAsyncThunk(
  "shadowTeam/exportPdf",
  async ({ shadowTeamId, locale = "en" }, { rejectWithValue }) => {
    try {
      const response = await $apiV2.post(
        `/users/shadow-teams/${shadowTeamId}/export/pdf`,
        { locale },
        { responseType: "blob" }
      );
      trackShadowTeamAction({
        action: "export",
        metadata: {
          locale,
        },
        shadowTeamId: Number(shadowTeamId),
        sourceSurface: "shadow_team",
      });
      return response;
    } catch (error) {
      return rejectWithValue(getErrorMessage(error, "Failed to export shadow team PDF."));
    }
  }
);
